// @flow
import type { Pet } from '@rover/react-lib/types/Pet';
import { createDuck } from '@rover/react-lib/lib/redux-duck';
import type {
  ActionCreator,
  EffectCreator,
  SelectorCreator,
  DuckOptions,
  Duck,
} from '@rover/react-lib/lib/redux-duck';
import { get, save } from '@rover/react-lib/apis/personSummary.api';
import { type Address } from '@rover/react-lib/types/Address';

export type State = {
  summary: any,
  errorMessage: string,
  successMessage: string,
  loading: boolean,
};

type Actions = {
  fetchPersonSummaryPending: ActionCreator<void>,
  fetchPersonSummarySuccess: ActionCreator<any>,
  fetchPersonSummaryFailure: ActionCreator<{ message: string }>,
  savePersonSummaryPrivatePending: ActionCreator<void>,
  savePersonSummaryPrivateSuccess: ActionCreator<any>,
  savePersonSummaryPrivateFailure: ActionCreator<{ message: string }>,
};

type Effects = {
  fetchPersonSummary: EffectCreator<?string>,
  savePersonSummaryPrivate: EffectCreator<{ personData: any, personUrl: string }>,
};

type Selectors = {
  getPersonSummary: SelectorCreator<any>,
  getErrorMessage: SelectorCreator<string>,
  getPersonUrl: SelectorCreator<string>,
  getIsLoading: SelectorCreator<boolean>,
  getIsSuccessful: SelectorCreator<boolean>,
  getAvailablePets: SelectorCreator<Array<Pet>>,
  getAddress: SelectorCreator<?Address>,
  getOpk: SelectorCreator<?string>,
  getEmergencyContactName: SelectorCreator<string>,
  getEmergencyContactPhoneNumber: SelectorCreator<string>,
  getServices: SelectorCreator<Array<any>>,
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'personSummary',
  initialState: {
    summary: {
      pets: [],
    },
    errorMessage: '',
    successMessage: '',
    loading: false,
  },
  actions: {
    fetchPersonSummaryPending: () => state => ({
      ...state,
      loading: true,
    }),
    fetchPersonSummarySuccess: summary => state => ({
      ...state,
      loading: false,
      summary,
      errorMessage: '',
    }),
    fetchPersonSummaryFailure: ({ message }) => state => ({
      ...state,
      loading: false,
      errorMessage: message,
    }),
    savePersonSummaryPrivatePending: () => state => ({
      ...state,
      loading: true,
    }),
    savePersonSummaryPrivateSuccess: summaryPrivate => state => ({
      ...state,
      loading: false,
      summary: { ...state.summary, private: summaryPrivate },
      errorMessage: '',
    }),
    savePersonSummaryPrivateFailure: ({ message }) => state => ({
      ...state,
      loading: false,
      errorMessage: message,
    }),
  },
  effects: {
    fetchPersonSummary: (personUrl: ?string) => async (dispatch, getState, duckActions) => {
      dispatch(duckActions.fetchPersonSummaryPending());
      try {
        dispatch(duckActions.fetchPersonSummarySuccess(await get(personUrl)));
      } catch (error) {
        dispatch(duckActions.fetchPersonSummaryFailure(error));
      }
    },
    savePersonSummaryPrivate: ({ personData, personUrl }) => async (
      dispatch,
      getState,
      duckActions
    ) => {
      dispatch(duckActions.savePersonSummaryPrivatePending());
      try {
        dispatch(duckActions.savePersonSummaryPrivateSuccess(await save(personData, personUrl)));
      } catch (error) {
        dispatch(duckActions.savePersonSummaryPrivateFailure(error));
      }
    },
  },
  selectors: {
    getPersonSummary: (getState, createSelector) => createSelector([getState], s => s.summary),
    getPersonUrl: (getState, createSelector) => createSelector([getState], s => s.summary.url),
    getErrorMessage: (getState, createSelector) => createSelector([getState], s => s.errorMessage),
    getIsLoading: (getState, createSelector) => createSelector([getState], s => s.loading),
    getIsSuccessful: (getState, createSelector) =>
      createSelector([getState], s => !s.loading && !s.errorMessage),
    getAvailablePets: (getState, createSelector) => createSelector([getState], s => s.summary.pets),
    getAddress: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        personSummary => personSummary && personSummary.private && personSummary.private.home
      ),
    getOpk: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        personSummary =>
          personSummary &&
          personSummary.url &&
          personSummary.url
            .substr(0, personSummary.url.length - 1)
            .split('/')
            .pop()
      ),
    getEmergencyContactName: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        personSummary =>
          personSummary && personSummary.private && personSummary.private.emergency_contact_name
      ),
    getEmergencyContactPhoneNumber: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        personSummary =>
          personSummary &&
          personSummary.private &&
          personSummary.private.emergency_contact_phone_number
      ),
    getServices: (getState, createSelector, { getPersonSummary }) =>
      createSelector([getPersonSummary], personSummary => personSummary && personSummary.services),
  },
};

export const { actions, selectors, effects, reducer } = (createDuck(options): Duck<
  State,
  Actions,
  Effects,
  Selectors
>);



// WEBPACK FOOTER //
// ../react-lib/src/redux/ducks/personSummary.duck.js